<template>
  <div ref="root" class="no-plan-menu" role="group" aria-label="Create allocation for this week" @keydown.escape.prevent="emit('close')">
    <button v-if="previousWeekPlan" type="button" class="no-plan-menu__copy" @click="emit('copy')">
      <span aria-hidden="true">⟳</span> Copy previous week ({{ copyLabel }})
    </button>
    <div class="no-plan-menu__row">
      <input
        ref="inputEl"
        v-model.number="typedCapacity"
        type="number"
        min="0"
        step="any"
        class="input no-plan-menu__input"
        placeholder="Capacity"
        aria-label="Capacity"
        @keydown.enter.prevent="onAdd"
      />
      <button type="button" class="btn-primary no-plan-menu__add" :disabled="!isValid" @click="onAdd">Add</button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

  import { formatCapacityQuantity } from '@/utils/capacityCalendar';
  import type { PreviousWeekPlan } from '@/utils/previousWeek';

  const props = defineProps<{
    // The row's capacity unit (e.g. "parts") — null for termination rows.
    unit?: string | null;
    // Resolved by the parent via resolvePreviousWeekPlan; null hides the copy line entirely.
    previousWeekPlan: PreviousWeekPlan | null;
  }>();

  const emit = defineEmits<{
    copy: [];
    add: [capacity: number];
    close: [];
  }>();

  const root = ref<HTMLElement | null>(null);
  const inputEl = ref<HTMLInputElement | null>(null);
  const typedCapacity = ref<number | null>(null);

  const copyLabel = computed(() => {
    const plan = props.previousWeekPlan;
    if (!plan) return '';
    const quantity = formatCapacityQuantity(plan.capacity, props.unit ?? null);
    return plan.capacityFactor === null ? quantity : `${quantity} · factor ${plan.capacityFactor}`;
  });

  const isValid = computed(() => typeof typedCapacity.value === 'number' && Number.isFinite(typedCapacity.value) && typedCapacity.value > 0);

  function onAdd(): void {
    if (!isValid.value || typedCapacity.value === null) return;
    emit('add', typedCapacity.value);
  }

  // Clicking anywhere outside the menu closes it, mirroring Esc (spec §4.3).
  function onDocumentMouseDown(event: MouseEvent): void {
    if (root.value && !root.value.contains(event.target as Node)) {
      emit('close');
    }
  }

  onMounted(async () => {
    document.addEventListener('mousedown', onDocumentMouseDown);
    await nextTick();
    inputEl.value?.focus();
  });

  onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocumentMouseDown);
  });
</script>

<style scoped>
  .no-plan-menu {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: center;
    gap: 0.375rem;
    padding: 0.375rem 0.5rem;
  }

  .no-plan-menu__copy {
    overflow: hidden;
    padding: 0.125rem 0.25rem;
    border: 1px dashed rgb(148 163 184);
    border-radius: 0.25rem;
    background: rgb(239 246 255);
    color: rgb(29 78 216);
    font-size: 0.625rem;
    font-weight: 600;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .no-plan-menu__copy:hover {
    background: rgb(219 234 254);
  }

  .no-plan-menu__row {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .no-plan-menu__input {
    min-width: 0;
    flex: 1;
    padding: 0.1875rem 0.375rem;
    font-size: 0.6875rem;
  }

  .no-plan-menu__add {
    padding: 0.1875rem 0.5rem;
    font-size: 0.6875rem;
  }
</style>
